import * as React from "react";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Input, Popover } from "antd";
import type { FormattingFeatureSection } from "@/Popups/contentSlide/newPrecedentSlide/steps/formattingFeaturesConfig";
import { FeatureImageButton } from "./FeatureImageButton";

export type FormattingFeatureContextHelpFlow = "precedentSlide" | "slideEdit";

const CONTEXT_HELP_BY_FLOW: Record<FormattingFeatureContextHelpFlow, string> = {
  precedentSlide:
    "Type a response to the question for any context you want Alpha Agent to consider when creating this feature in Excel. Leave blank and Alpha Agent will make an independent assessment for how best to implement the feature.",
  slideEdit:
    "Type Instructions you want Alpha Agent to implement when creating this feature in Excel. Leave blank and Alpha Agent will make an independent assessment for how best to implement the feature.",
};

function formatMainQuestionWithCompany(mainQuestion: string, companyName: string): string {
  const possessive = `${companyName}'s`;
  return mainQuestion.replace(/\[[^\]]*\]/g, possessive);
}

export type FormattingFeatureQuestionBlockProps = {
  section: FormattingFeatureSection;
  chartIndex: number;
  value: boolean | null;
  onSetValue: (yes: boolean) => void;
  userInput?: string;
  onUserInputChange?: (text: string) => void;
  primaryCompanyName?: string | null;
  contextHelpFlow?: FormattingFeatureContextHelpFlow;
};

export function FormattingFeatureQuestionBlock({
  section,
  chartIndex,
  value,
  onSetValue,
  userInput,
  onUserInputChange,
  primaryCompanyName,
  contextHelpFlow = "precedentSlide",
}: FormattingFeatureQuestionBlockProps) {
  const contextHelpText = CONTEXT_HELP_BY_FLOW[contextHelpFlow];
  const [editingDetail, setEditingDetail] = React.useState(false);

  React.useEffect(() => {
    if (value !== true) setEditingDetail(false);
  }, [value]);

  const resolvedCompanyName = primaryCompanyName?.trim() || "your company";
  const displayMainQuestion = formatMainQuestionWithCompany(section.mainQuestion, resolvedCompanyName);

  const showUserInput = value === true && userInput !== undefined && onUserInputChange && section.mainQuestion !== "";

  return (
    <div className="px-4">
      <p className="mb-3 text-sm font-bold text-gray-800">{section.question}</p>
      <div className="flex w-full flex-wrap justify-center gap-4">
        {section.options.map((opt, optionIndex) => {
          const isYes = optionIndex === 0;
          const selected = isYes ? value === true : value === false;
          return (
            <FeatureImageButton
              key={`${chartIndex}-${opt.dataIndex}`}
              title={opt.title}
              subtitle={opt.subtitle}
              image={opt.image}
              selected={selected}
              onClick={() => onSetValue(isYes)}
            />
          );
        })}
      </div>

      {showUserInput ? (
        <div className="mt-3 flex w-full items-center gap-2">
          <div className="w-full">
            {editingDetail ? (
              <Input.TextArea
                autoFocus
                className="rounded-md border border-[#ff5c35] !text-sm"
                value={userInput}
                placeholder={displayMainQuestion}
                onChange={(e) => onUserInputChange(e.target.value)}
                onBlur={() => setEditingDetail(false)}
                rows={6}
                autoSize={{ minRows: 2, maxRows: 16 }}
              />
            ) : (
              <button
                type="button"
                className="w-full cursor-text rounded-md border border-[#ff5c35] bg-white px-4 py-2 text-left text-sm whitespace-pre-line hover:bg-orange-50/40"
                onClick={() => setEditingDetail(true)}
              >
                {userInput.trim() !== "" ? (
                  <span className="text-gray-800">{userInput}</span>
                ) : (
                  <span className="text-gray-400 italic">{displayMainQuestion}</span>
                )}
              </button>
            )}
          </div>
          <div className="flex shrink-0 justify-end self-start pt-1">
            <Popover
              content={
                <p className="m-0 max-w-sm text-sm text-gray-700">{contextHelpText}</p>
              }
              trigger="click"
              overlayStyle={{ maxWidth: 400 }}
            >
              <button
                type="button"
                className="inline-flex items-center justify-center rounded p-0.5 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
                aria-label="Alpha Agent context for this feature"
              >
                <InfoCircleOutlined className="text-lg" />
              </button>
            </Popover>
          </div>
        </div>
      ) : null}
    </div>
  );
}
